<script setup>
/**
 * @fileoverview SharePortalConsole - Full-screen VM console for external users
 *
 * Full-screen console for external users accessing shared labs.
 * Uses native Guacamole integration via ConsoleViewer component.
 * Includes toolbar with clipboard and Ctrl+Alt+Del support.
 * Displays session expiry warnings and provides back navigation.
 *
 * @example
 * Route: /share/:shareId/vms/:vmId/console
 */

import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AlertTriangle, Loader2, RefreshCw, Clock } from 'lucide-vue-next'
import { useShareSession } from '@/composables/useShareSession'
import { getShareSessionToken } from '@/api/client'
import { useConsole } from '@/composables/useConsole'
import ConsoleToolbar from '@/components/console/ConsoleToolbar.vue'
import Button from '@/components/ui/button/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import AlertDescription from '@/components/ui/AlertDescription.vue'

const route = useRoute()
const router = useRouter()

// Get route params
const shareId = computed(() => route.params.shareId)
const vmId = computed(() => route.params.vmId)

// State
const vmName = ref('')
const displayRef = ref(null)
const consoleContainerRef = ref(null)

// Share session composable
const { labName, timeRemainingFormatted, isExpiringSoon, checkSession } = useShareSession()

// Console composable with auto-reconnect
const {
  initializeClient,
  connectShare,
  reconnect,
  connectionState,
  errorMessage,
  reconnectAttempts,
  sendClipboard,
  sendCtrlAltDel,
  setKeyboardEnabled
} = useConsole({
  maxReconnectAttempts: 5,
  initialReconnectDelay: 1000,
  maxReconnectDelay: 30000
})

/**
 * Check authentication and load console on mount
 */
onMounted(async () => {
  // Check if session is valid
  if (!checkSession()) {
    // Will redirect to login automatically
    return
  }

  // Wait for DOM to be fully rendered with proper dimensions
  await nextTick()

  // Verify display element exists and has dimensions
  if (!displayRef.value) {
    throw new Error('Display element not available')
  }

  const rect = displayRef.value.getBoundingClientRect()
  console.debug('[SharePortalConsole] Display element dimensions after nextTick:', {
    width: rect.width,
    height: rect.height
  })

  // If dimensions are still 0, wait a bit for CSS to apply
  if (rect.width === 0 || rect.height === 0) {
    console.debug('[SharePortalConsole] Display has zero dimensions, waiting for layout...')
    await new Promise(resolve => setTimeout(resolve, 100))
    const newRect = displayRef.value.getBoundingClientRect()
    console.debug('[SharePortalConsole] Display dimensions after delay:', {
      width: newRect.width,
      height: newRect.height
    })
  }

  // Initialize Guacamole client
  initializeClient(displayRef.value)

  // Get share session token from sessionStorage
  const token = getShareSessionToken()
  if (!token) {
    console.error('[SharePortalConsole] No share session token found')
    router.push(`/share/${shareId.value}/login`)
    return
  }

  // Connect to share console WebSocket
  // Two-step process: fetch token from central API, then connect to regional WebSocket
  console.debug('[SharePortalConsole] Connecting to share console...')
  await connectShare(shareId.value, vmId.value, token)
})

/**
 * Cleanup on unmount
 */
onBeforeUnmount(() => {
  // Console cleanup is handled by useConsole composable
})

/**
 * Navigate back to VM list
 */
function goBack() {
  router.push(`/share/${shareId.value}/vms`)
}

/**
 * Retry console connection
 */
async function handleRetry() {
  // Just reconnect with existing parameters
  reconnect()
}

/**
 * Get connection state text
 * @returns {string} Display text for current state
 */
function getStateText() {
  if (connectionState.value === 'reconnecting') {
    return `Reconnecting (attempt ${reconnectAttempts.value}/5)...`
  }

  const states = {
    idle: 'Initializing...',
    connecting: 'Connecting to console...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Connection Error'
  }
  return states[connectionState.value] || 'Unknown'
}

/**
 * Check if maximum reconnect attempts exceeded
 * @returns {boolean} True if should show retry button
 */
const isReconnectExceeded = computed(() => {
  return connectionState.value === 'error' && reconnectAttempts.value >= 5
})

/**
 * Handle send clipboard from toolbar
 * @param {string} text - Text to send to remote clipboard
 */
function handleSendClipboard(text) {
  sendClipboard(text)
}

/**
 * Handle Ctrl+Alt+Del from toolbar
 */
function handleCtrlAltDel() {
  sendCtrlAltDel()
}

/**
 * Handle fullscreen toggle
 * @param {boolean} isFullscreen - Whether to enter or exit fullscreen
 */
async function handleFullscreen(isFullscreen) {
  if (!consoleContainerRef.value) {
    return
  }

  try {
    if (isFullscreen) {
      if (consoleContainerRef.value.requestFullscreen) {
        await consoleContainerRef.value.requestFullscreen()
      } else if (consoleContainerRef.value.webkitRequestFullscreen) {
        await consoleContainerRef.value.webkitRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      }
    }
  } catch (err) {
    console.error('[SharePortalConsole] Fullscreen error:', err)
  }
}

/**
 * Handle dialog open - disable keyboard capture so user can type in dialog
 */
function handleDialogOpen() {
  setKeyboardEnabled(false)
}

/**
 * Handle dialog close - re-enable keyboard capture for console
 */
function handleDialogClose() {
  setKeyboardEnabled(true)
}
</script>

<template>
  <div
    ref="consoleContainerRef"
    class="h-screen w-screen bg-gray-900 flex flex-col overflow-hidden"
  >
    <!-- Console Header with Toolbar -->
    <div class="flex-shrink-0 flex items-center bg-gray-800 border-b border-gray-700">
      <!-- Console Toolbar (clipboard, Ctrl+Alt+Del, fullscreen) -->
      <div class="flex-1">
        <ConsoleToolbar
          :vm-name="vmName || 'Console'"
          :connection-state="connectionState"
          :reconnect-attempts="reconnectAttempts"
          @back="goBack"
          @fullscreen="handleFullscreen"
          @sendClipboard="handleSendClipboard"
          @sendCtrlAltDel="handleCtrlAltDel"
          @dialogOpen="handleDialogOpen"
          @dialogClose="handleDialogClose"
        />
      </div>

      <!-- Session Info (Share Portal specific) -->
      <div class="flex items-center gap-3 px-4 border-l border-gray-700">
        <!-- Session Expiry Warning -->
        <Alert v-if="isExpiringSoon" variant="destructive" class="py-1 px-3">
          <AlertTriangle class="w-3 h-3" />
          <AlertDescription class="text-xs">
            {{ timeRemainingFormatted }} remaining
          </AlertDescription>
        </Alert>

        <!-- Session Timer (Normal) -->
        <div v-else class="flex items-center gap-2 text-sm text-gray-400">
          <Clock class="w-4 h-4" />
          <span>{{ timeRemainingFormatted }}</span>
        </div>
      </div>
    </div>

    <!-- Console Content -->
    <div class="flex-1 relative bg-black overflow-hidden">
      <!-- Connecting State -->
      <div
        v-if="connectionState === 'idle' || connectionState === 'connecting'"
        class="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white z-10"
      >
        <Loader2 class="w-12 h-12 animate-spin text-indigo-500 mb-4" />
        <p class="text-lg font-medium">{{ getStateText() }}</p>
        <p class="text-sm text-gray-400 mt-2">Establishing connection...</p>
      </div>

      <!-- Reconnecting State -->
      <div
        v-else-if="connectionState === 'reconnecting'"
        class="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/95 text-white z-10"
      >
        <RefreshCw class="w-12 h-12 animate-spin text-amber-500 mb-4" />
        <p class="text-lg font-medium">{{ getStateText() }}</p>
        <p class="text-sm text-gray-400 mt-2">Attempting to reconnect...</p>
      </div>

      <!-- Error State (Max Reconnect Attempts) -->
      <div
        v-else-if="isReconnectExceeded"
        class="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white z-10 p-8"
      >
        <AlertTriangle class="w-16 h-16 text-red-500 mb-4" />
        <h3 class="text-xl font-semibold mb-2">Connection Lost</h3>
        <p class="text-gray-300 text-center max-w-md mb-4">
          {{ errorMessage || 'Unable to establish console connection after multiple attempts' }}
        </p>
        <div class="flex items-center gap-3">
          <Button @click="handleRetry" class="gap-2">
            <RefreshCw class="w-4 h-4" />
            Retry Connection
          </Button>
          <Button variant="outline" @click="goBack"> Back to VMs </Button>
        </div>
      </div>

      <!-- Connection Error (Initial) -->
      <div
        v-else-if="connectionState === 'error' && !isReconnectExceeded"
        class="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white z-10 p-8"
      >
        <AlertTriangle class="w-16 h-16 text-red-500 mb-4" />
        <h3 class="text-xl font-semibold mb-2">Connection Failed</h3>
        <p class="text-gray-300 text-center max-w-md mb-4">
          {{ errorMessage || 'Unable to establish console connection' }}
        </p>
        <Button @click="handleRetry" class="gap-2">
          <RefreshCw class="w-4 h-4" />
          Retry Connection
        </Button>
      </div>

      <!-- Guacamole Display Container -->
      <div
        ref="displayRef"
        class="guacamole-display absolute inset-0 flex items-center justify-center overflow-hidden"
        :class="{ 'opacity-0': connectionState !== 'connected' }"
      >
        <!-- Guacamole canvas will be appended here by the library -->
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure console fills container and handles overflow properly */
.guacamole-display {
  transition: opacity 0.3s ease-in-out;
}

/* Style the Guacamole canvas */
.guacamole-display :deep(canvas) {
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  /* Canvas will be scaled by Guacamole.Display.scale() and centered by flexbox */
}
</style>
